import React from 'react';

/**
 * Button
 * ------------------------------------------------------------------
 * Generic, reusable button used across the entire product, not just
 * onboarding. Supports visual variants, sizes, left/right icons,
 * a loading state, and forwards any native <button> prop (onClick,
 * type, disabled, aria-*, etc).
 *
 * Icons are passed as image paths (Figma exports) and rendered as
 * plain <img> tags — no inline SVG is generated here.
 *
 * @param {'primary'|'secondary'|'ghost'|'danger'|'toned'} variant
 * @param {'sm'|'md'|'lg'} size
 * @param {string} iconSrc - path to a leading icon image
 * @param {string} iconAlt - alt text for the leading icon
 * @param {string} trailingIconSrc - path to a trailing icon image (e.g. arrow)
 * @param {string} trailingIconAlt - alt text for the trailing icon
 * @param {boolean} loading - shows a spinner and disables interaction
 * @param {boolean} fullWidth - stretches the button to 100% width
 * @param {string} tone - color tone for 'toned' variant (red, darkgreen, purple-custom, etc)
 */
const CustomButton = ({
  children,
  variant = 'primary',
  size = 'md',
  iconSrc,
  iconAlt = '',
  trailingIconSrc,
  trailingIconAlt = '',
  loading = false,
  fullWidth = false,
  disabled = false,
  tone = '',
  className = '',
  type = 'button',
  innerRef,
  ...rest
}) => {
  const classes = [
    'amok-btn',
    `amok-btn--${variant}`,
    `amok-btn--${size}`,
    fullWidth ? 'amok-btn--full' : '',
    loading ? 'amok-btn--loading' : '',
    tone ? `amok-btn--${tone}` : '',
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={innerRef}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="amok-btn__spinner" aria-hidden="true" />}
      {!loading && iconSrc && (
        <img
          className="amok-btn__icon amok-btn__icon--leading"
          src={iconSrc}
          alt={iconAlt}
        />
      )}
      <span className="amok-btn__label">{children}</span>
      {!loading && trailingIconSrc && (
        <img
          className="amok-btn__icon amok-btn__icon--trailing"
          src={trailingIconSrc}
          alt={trailingIconAlt}
        />
      )}
    </button>
  );
};

export default CustomButton;